import apiV2Service from '../api/api-v2.service';
import {GetContentFeedItemsResponse} from '../contentFeedItems/contentFeedItems.types';
import waitForJob from '../jobs/utils/waitForJob';
import {MacroItem} from '../macros/macros.types';
import {ContentSet, RemoteContentSet} from './contentSets.types';
import {RemoteContentFeedHistoryUpdate} from '../contentFeeds/contentFeeds.types';
import {toClientFeedUpdateHistory} from '../contentFeeds/contentFeeds.dataConverters';
import {JobContentSetResult} from '../jobs/jobs.types';
import {toClientContentSet} from './contentSets.dataConverter';

const BASE_PATH = 'ContentSets';

async function getContentSet(feedId: number, contentSetId: number) {
  return apiV2Service
    .executeGetRequest<RemoteContentSet>(`${BASE_PATH}/${feedId}/GetContentSet/${contentSetId}`)
    .then(({data}) => toClientContentSet(data));
}

async function getContentSets(feedId: number): Promise<ContentSet[]> {
  const {data: remoteContentSets} = await apiV2Service.executeGetRequest<RemoteContentSet[]>(
    `${BASE_PATH}/${feedId}/GetContentSets`,
  );

  return remoteContentSets.map(toClientContentSet);
}

async function createContentSet(
  contentSet: Omit<ContentSet, 'id' | 'lastPopulationDate' | 'usedInCampaigns' | 'itemsCount'>,
) {
  return apiV2Service
    .executePutRequest<{contentSetId: number; updateItemsJobId: number}>(
      `${BASE_PATH}/CreateContentSet`,
      {},
      {
        ...contentSet,
      },
    )
    .then(({data: {updateItemsJobId}}) =>
      waitForJob<JobContentSetResult>({
        jobId: updateItemsJobId,
        jobName: 'Creating content set',
      }),
    );
}

async function updateContentSet(
  contentSet: Omit<ContentSet, 'lastPopulationDate' | 'usedInCampaigns' | 'itemsCount' | 'feedId'>,
) {
  return apiV2Service
    .executePutRequest<number>(
      `${BASE_PATH}/UpdateContentSet`,
      {},
      {
        ...contentSet,
      },
    )
    .then(({data: updateItemsJobId}) =>
      waitForJob<JobContentSetResult>({
        jobId: updateItemsJobId,
        jobName: 'Updating content set',
      }),
    );
}

async function getContentSetSignals(feedId: number) {
  return apiV2Service.executeGetRequest<MacroItem[]>(`${BASE_PATH}/${feedId}/signals`);
}

async function getSignalSuggestions(feedId: number, signalKey: string, searchPattern: string) {
  return apiV2Service.executeGetRequest<string[]>(`${BASE_PATH}/${feedId}/suggestions`, {
    signalKey,
    searchPattern,
  });
}

async function deleteContentSets(feedId: number, contentSetIds: number[]) {
  return apiV2Service
    .executeDeleteRequest<number>(`${BASE_PATH}/${feedId}/DeleteContentSets`, {
      contentSetIds,
    })
    .then(({data: jobId}) =>
      waitForJob({
        jobId,
        jobName: 'Deleting content sets',
      }),
    );
}

async function getContentSetItems(feedId: number, contentSetId: number) {
  return apiV2Service
    .executeGetRequest<GetContentFeedItemsResponse>(
      `${BASE_PATH}/${feedId}/GetContentSetItems/${contentSetId}`,
    )
    .then(({data}) => data);
}

async function fetchHistoryUpdates(setId: number) {
  return apiV2Service
    .executeGetRequest<RemoteContentFeedHistoryUpdate[]>(`${BASE_PATH}/${setId}/updateHistory`)
    .then(({data: historyUpdates}) => historyUpdates.map(toClientFeedUpdateHistory));
}

async function searchContentSets(prompt: string) {
  const {data} = await apiV2Service.executeGetRequest<{
    contentSets: Array<{id: number; feedId: number; name: string}>;
    filtersExplanation: string;
  }>(`${BASE_PATH}/SearchContentSets`, {prompt});

  return data;
}

export const contentSetsApi = {
  getContentSet,
  getContentSets,
  createContentSet,
  updateContentSet,
  getContentSetSignals,
  getSignalSuggestions,
  deleteContentSets,
  getContentSetItems,
  fetchHistoryUpdates,
  searchContentSets,
};
